"use client"

import { useState } from "react"
import { Eye, EyeOff, AlertCircle, CheckCircle, ChevronDown } from "lucide-react"
import SideSection from "../constant/SideSection"
import { useNavigate } from "react-router-dom"
import { useSignup } from "../hooks/useRegister"

// Country codes for phone picker
const countryCodes = [
  { code: "+234", country: "NG", flag: "🇳🇬", name: "Nigeria" },
  { code: "+1", country: "US", flag: "🇺🇸", name: "United States" },
  { code: "+44", country: "GB", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+91", country: "IN", flag: "🇮🇳", name: "India" },
  { code: "+86", country: "CN", flag: "🇨🇳", name: "China" },
]

export default function RegisterForm() {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [mobileNumber, setMobileNumber] = useState("")
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryCodes[0]) // Default to Nigeria
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState("")

  const navigate = useNavigate()
  const { signup, loading, error, clearError } = useSignup()

  const validateField = (name, value) => {
    const errors = { ...fieldErrors }

    switch (name) {
      case "firstName":
        if (!value.trim()) {
          errors.firstName = "First name is required"
        } else {
          delete errors.firstName
        }
        break
      case "lastName":
        if (!value.trim()) {
          errors.lastName = "Last name is required"
        } else {
          delete errors.lastName
        }
        break
      case "email":
        if (!value.trim()) {
          errors.email = "Email is required"
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          errors.email = "Please enter a valid email address"
        } else {
          delete errors.email
        }
        break
      case "mobileNumber":
        if (!value.trim()) {
          errors.mobileNumber = "Mobile number is required"
        } else if (value.trim().length < 10) {
          errors.mobileNumber = "Please enter a valid mobile number"
        } else {
          delete errors.mobileNumber
        }
        break
      case "password":
        if (!value) {
          errors.password = "Password is required"
        } else if (value.length < 8) {
          errors.password = "Password must be at least 8 characters long"
        } else {
          delete errors.password
        }
        // Re-validate confirm password if it exists
        if (confirmPassword && value !== confirmPassword) {
          errors.confirmPassword = "Passwords do not match"
        } else if (confirmPassword && value === confirmPassword) {
          delete errors.confirmPassword
        }
        break
      case "confirmPassword":
        if (!value) {
          errors.confirmPassword = "Please confirm your password"
        } else if (password !== value) {
          errors.confirmPassword = "Passwords do not match"
        } else {
          delete errors.confirmPassword
        }
        break
      default:
        break
    }

    setFieldErrors(errors)
  }

  const handleInputChange = (name, value) => {
    // Clear general error and success message when user starts typing
    if (error) clearError()
    if (successMessage) setSuccessMessage("")

    // Update state
    switch (name) {
      case "firstName":
        setFirstName(value)
        break
      case "lastName":
        setLastName(value)
        break
      case "email":
        setEmail(value)
        break
      case "mobileNumber":
        setMobileNumber(value)
        break
      case "password":
        setPassword(value)
        break
      case "confirmPassword":
        setConfirmPassword(value)
        break
      default:
        break
    }

    // Validate field
    validateField(name, value)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Clear any previous messages
    clearError()
    setSuccessMessage("")

    // Prepare form data with full phone number
    const fullPhoneNumber = selectedCountryCode.code + mobileNumber.trim()
    
    const formData = {
      firstName,
      lastName,
      email,
      mobile: fullPhoneNumber,
      password,
      confirmPassword,
      agreeToTerms,
    }

    const result = await signup(formData)

    if (result.success) {
      console.log("Registration successful:", result.data)
      setSuccessMessage("Account created successfully! Please check your email to verify your account before logging in.")
      
      // Clear form
      setFirstName("")
      setLastName("")
      setEmail("")
      setMobileNumber("")
      setPassword("")
      setConfirmPassword("")
      setAgreeToTerms(false)
      setFieldErrors({})
      
      // Navigate to login page after a delay
      setTimeout(() => {
        navigate('/auth/login')
      }, 3000)
    } else {
      console.log("Registration failed:", result.error)
    }
  }

  const isFormValid = () => {
    return (
      firstName.trim() &&
      lastName.trim() &&
      email.trim() &&
      mobileNumber.trim() &&
      password &&
      confirmPassword &&
      agreeToTerms &&
      Object.keys(fieldErrors).length === 0
    )
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen overflow-y-hidden">
      {/* Side Section - only visible on large screens */}
      <div className="hidden w-1/2 lg:block overflow-hidden">
        <SideSection imageUrl="/images/auth.png" />
      </div>

      {/* Main Content */}
      <div className="flex-1 lg:w-1/2 flex flex-col min-h-screen overflow-hidden">
        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-[32rem] md:p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <div>
                  <img src="/images/logo.png" className="w-20" alt="PULO Logo" />
                </div>
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Create Your PULO Account</h1>
              <p className="text-gray-600 text-sm">
                Join PULO to discover unforgettable destinations, connect with trusted travel assistants, and book your
                perfect stay.
              </p>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-green-700 text-sm">{successMessage}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="block text-sm font-medium text-gray-700 mb-2">
                    First name <span className="text-red-500">*</span>
                  </div>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    placeholder="Enter your first name"
                    className={`w-full px-3 py-3 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent placeholder-gray-400 ${
                      fieldErrors.firstName
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300 focus:ring-purple-500"
                    }`}
                    disabled={loading}
                    required
                  />
                  {fieldErrors.firstName && <p className="mt-1 text-sm text-red-600">{fieldErrors.firstName}</p>}
                </div>
                <div>
                  <div className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </div>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    placeholder="Enter your last name"
                    className={`w-full px-3 py-3 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent placeholder-gray-400 ${
                      fieldErrors.lastName
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300 focus:ring-purple-500"
                    }`}
                    disabled={loading}
                    required
                  />
                  {fieldErrors.lastName && <p className="mt-1 text-sm text-red-600">{fieldErrors.lastName}</p>}
                </div>
              </div>

              {/* Email Field */}
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="Enter your email address"
                  className={`w-full px-3 py-3 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent placeholder-gray-400 ${
                    fieldErrors.email ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-purple-500"
                  }`}
                  disabled={loading}
                  required
                />
                {fieldErrors.email && <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>}
              </div>

              {/* Mobile Number Field with Country Code Picker */}
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number <span className="text-red-500">*</span>
                </div>
                <div className="flex">
                  {/* Country Code Picker */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      className={`flex items-center px-3 py-3 border border-r-0 rounded-l-md bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        fieldErrors.mobileNumber ? "border-red-300" : "border-gray-300"
                      }`}
                      disabled={loading}
                    >
                      <span className="mr-2">{selectedCountryCode.flag}</span>
                      <span className="text-sm font-medium">{selectedCountryCode.code}</span>
                      <ChevronDown className="w-4 h-4 ml-1 text-gray-500" />
                    </button>
                    
                    {/* Dropdown */}
                    {showCountryDropdown && (
                      <div className="absolute top-full left-0 z-10 bg-white border border-gray-300 rounded-md shadow-lg mt-1 w-64">
                        {countryCodes.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            onClick={() => {
                              setSelectedCountryCode(country)
                              setShowCountryDropdown(false)
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center"
                          >
                            <span className="mr-3">{country.flag}</span>
                            <span className="mr-2 font-medium">{country.code}</span>
                            <span className="text-sm text-gray-600">{country.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) => handleInputChange("mobileNumber", e.target.value)}
                    placeholder="Enter your phone number"
                    className={`flex-1 px-3 py-3 border rounded-r-md focus:outline-none focus:ring-2 focus:border-transparent placeholder-gray-400 ${
                      fieldErrors.mobileNumber
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300 focus:ring-purple-500"
                    }`}
                    disabled={loading}
                    required
                  />
                </div>
                {fieldErrors.mobileNumber && <p className="mt-1 text-sm text-red-600">{fieldErrors.mobileNumber}</p>}
              </div>

              {/* Password Field */}
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Password <span className="text-red-500">*</span>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    placeholder="Enter password"
                    className={`w-full px-3 py-3 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent placeholder-gray-400 ${
                      fieldErrors.password
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300 focus:ring-purple-500"
                    }`}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {fieldErrors.password && <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>}
              </div>

              {/* Confirm Password Field */}
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </div>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    placeholder="Confirm password"
                    className={`w-full px-3 py-3 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent placeholder-gray-400 ${
                      fieldErrors.confirmPassword
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300 focus:ring-purple-500"
                    }`}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>
                )}
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start space-x-3 py-4">
                <input
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mt-1"
                  disabled={loading}
                  required
                />
                <div className="text-sm text-gray-600 leading-relaxed">
                  I agree to PULO's{" "}
                  <button type="button" className="text-purple-600 hover:underline">
                    Terms of Use
                  </button>{" "}
                  and consent to{" "}
                  <button type="button" className="text-purple-600 hover:underline">
                    Privacy Policy
                  </button>
                </div>
              </div>
              {fieldErrors.agreeToTerms && <p className="text-sm text-red-600">{fieldErrors.agreeToTerms}</p>}

              {/* Create Account Button */}
              <button
                type="submit"
                disabled={loading || !isFormValid()}
                className={`w-full py-3 px-4 rounded-md font-medium transition duration-200 ${
                  loading || !isFormValid()
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : "bg-purple-800 text-white hover:bg-purple-900"
                }`}
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>

              {/* Login Link */}
              <div className="text-center pt-4">
                <span className="text-gray-600 text-sm">Already have an account? </span>
                <button
                  type="button"
                  onClick={() => navigate("/auth/login")}
                  className="text-purple-600 text-sm font-medium hover:underline"
                  disabled={loading}
                >
                  Log In
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer - Stays at bottom on large screens, resets on smaller screens */}
        <div className="lg:mt-0 mt-8">
          <div className="p-6 border-t border-gray-200 bg-white">
            <div className="text-center text-xs text-gray-500 space-x-4">
              <span>© 2024 PULO. All rights reserved.</span>
              <a href="#" className="hover:text-gray-700">
                Terms & Conditions
              </a>
              <a href="#" className="hover:text-gray-700">
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}